<template>
  <transition name="modal-fade">
    <div v-if="show && order" class="modal-overlay" @click="close">
      <div class="modal-container" @click.stop>
        <!-- Modal Header -->
        <div class="modal-header">
          <div class="header-content">
            <div class="header-left">
              <font-awesome-icon icon="fa-solid fa-file-invoice" class="header-icon"/>
              <div>
                <h5>Order Details</h5>
                <p class="order-number-text">{{ order.orderNumber }}</p>
              </div>
            </div>
            <button class="btn-close" @click="close">
              <font-awesome-icon icon="fa-solid fa-xmark"/>
            </button>
          </div>
          <div class="gradient-accent"></div>
        </div>

        <!-- Modal Body -->
        <div class="modal-body">
          <!-- Status & Type Section -->
          <div class="info-section">
            <div class="section-header">
              <font-awesome-icon icon="fa-solid fa-info-circle"/>
              <h6>Order Status</h6>
            </div>
            <div class="status-grid">
              <div class="status-badge-large" :class="`status-${getStatusType(order.metadata?.status)}`">
                <font-awesome-icon :icon="getStatusIcon(order.metadata?.status)"/>
                <span>{{ formatStatusText(order.metadata?.status) }}</span>
              </div>
              <div class="type-badge">
                <font-awesome-icon :icon="order.metadata?.type === 'DELIVERY' ? 'fa-solid fa-truck' : 'fa-solid fa-shopping-bag'"/>
                {{ order.metadata?.type || 'PICKUP' }}
              </div>
            </div>
            <div v-if="order.metadata?.scheduledFor" class="scheduled-info">
              <font-awesome-icon icon="fa-solid fa-calendar-alt"/>
              <span>Scheduled: {{ displayDate(order.metadata.scheduledFor) }}</span>
            </div>
          </div>

          <!-- Customer Information -->
          <div class="info-section" v-if="order.customer">
            <div class="section-header">
              <font-awesome-icon icon="fa-solid fa-user"/>
              <h6>Customer Information</h6>
            </div>
            <div class="info-grid">
              <div class="info-item">
                <span class="label">Name</span>
                <span class="value">{{ order.customer.name }}</span>
              </div>
              <div class="info-item" v-if="order.customer.email">
                <span class="label">Email</span>
                <span class="value">{{ order.customer.email }}</span>
              </div>
              <div class="info-item" v-if="order.customer.phone">
                <span class="label">Phone</span>
                <span class="value">{{ order.customer.phone }}</span>
              </div>
              <div class="info-item full-width" v-if="order.customer.shippingAddress">
                <span class="label">Shipping Address</span>
                <span class="value">{{ order.customer.shippingAddress }}</span>
              </div>
            </div>
          </div>

          <!-- Order Items -->
          <div class="info-section" v-if="order.items && order.items.length > 0">
            <div class="section-header">
              <font-awesome-icon icon="fa-solid fa-shopping-cart"/>
              <h6>Order Items ({{ order.items.length }})</h6>
            </div>
            <div class="items-list">
              <div v-for="(item, index) in order.items" :key="index" class="item-card">
                <div class="item-header">
                  <div class="item-name">{{ item.productName }}</div>
                  <div class="item-total">{{ formatMoney(item.lineTotal) }}</div>
                </div>
                <div class="item-details">
                  <span class="item-qty">{{ item.quantity }} × {{ formatMoney(item.unitPrice) }}</span>
                  <span v-if="item.productPlu" class="item-plu">PLU: {{ item.productPlu }}</span>
                </div>
                <div v-if="item.specialInstructions" class="item-instructions">
                  <template v-if="parseCookingInstructions(item.specialInstructions).length > 0">
                    <div class="instructions-header">
                      <font-awesome-icon icon="fa-solid fa-utensils"/>
                      Cooking Instructions
                    </div>
                    <div class="cooking-instructions">
                      <div v-for="(instruction, idx) in parseCookingInstructions(item.specialInstructions)" :key="idx" class="cooking-badge">
                        <span class="cooking-qty">{{ instruction.Qty }}×</span>
                        <span class="cooking-msg">{{ instruction.CookingMsg }}</span>
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <font-awesome-icon icon="fa-solid fa-sticky-note"/>
                    {{ item.specialInstructions }}
                  </template>
                </div>
              </div>
            </div>
          </div>

          <!-- Payment Information -->
          <div class="info-section" v-if="order.payment">
            <div class="section-header">
              <font-awesome-icon icon="fa-solid fa-credit-card"/>
              <h6>Payment Details</h6>
            </div>
            <div class="payment-grid">
              <div class="payment-status" :class="`payment-${order.payment.status?.toLowerCase()}`">
                <font-awesome-icon :icon="order.payment.status === 'PAID' ? 'fa-solid fa-check-circle' : 'fa-solid fa-clock'"/>
                {{ order.payment.status }}
              </div>
              <div class="payment-method">
                <font-awesome-icon :icon="getPaymentIcon(order.payment.method)"/>
                {{ order.payment.method }}
              </div>
            </div>
          </div>

          <!-- Pricing Breakdown -->
          <div class="info-section pricing-section" v-if="order.pricing">
            <div class="section-header">
              <font-awesome-icon icon="fa-solid fa-calculator"/>
              <h6>Pricing Breakdown</h6>
            </div>
            <div class="pricing-details">
              <div class="pricing-row">
                <span>Subtotal</span>
                <span>{{ formatMoney(order.pricing.subtotal) }}</span>
              </div>
              <div class="pricing-row" v-if="order.pricing.taxAmount">
                <span>Tax</span>
                <span>{{ formatMoney(order.pricing.taxAmount) }}</span>
              </div>
              <div class="pricing-row" v-if="order.pricing.deliveryFee">
                <span>Delivery Fee</span>
                <span>{{ formatMoney(order.pricing.deliveryFee) }}</span>
              </div>
              <div class="pricing-row total">
                <span>Total Amount</span>
                <span class="total-amount">{{ formatMoney(order.pricing.totalAmount) }}</span>
              </div>
            </div>
          </div>

          <!-- Notes -->
          <div class="info-section" v-if="order.metadata?.notes">
            <div class="section-header">
              <font-awesome-icon icon="fa-solid fa-comment-dots"/>
              <h6>Order Notes</h6>
            </div>
            <div class="notes-box">{{ order.metadata.notes }}</div>
          </div>

          <!-- Timestamps -->
          <div class="timestamps">
            <div class="timestamp-item">
              <font-awesome-icon icon="fa-solid fa-clock"/>
              <span>Created: {{ displayDate(order.createdAt) }}</span>
            </div>
            <div class="timestamp-item" v-if="order.updatedAt !== order.createdAt">
              <font-awesome-icon icon="fa-solid fa-sync-alt"/>
              <span>Updated: {{ displayDate(order.updatedAt) }}</span>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer">
          <button class="btn-secondary" @click="close">Close</button>
          <button class="btn-primary" @click="printOrder">
            <font-awesome-icon icon="fa-solid fa-print"/>
            Print
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'OrderDetailsModal',
  props: {
    show: { type: Boolean, default: false },
    order: { type: Object, default: null }
  },
  methods: {
    close() {
      this.$emit('close');
    },
    formatMoney(val) {
      try { return window.$utils.formatMoney(val || 0); } catch(e) { return val; }
    },
    displayDate(val) {
      try { return this.$dateTimeManager.formatDateTime(val); } catch(e) { return val; }
    },
    formatStatusText(status) {
      if (!status) return '';
      return status.split('_').map(word => word.charAt(0) + word.slice(1).toLowerCase()).join(' ');
    },
    getStatusType(status) {
      const statusMap = {
        'PENDING': 'pending', 'CONFIRMED': 'confirmed', 'PREPARING': 'preparing',
        'READY': 'ready', 'OUT_FOR_DELIVERY': 'delivering', 'COMPLETED': 'completed',
        'CANCELED': 'canceled', 'DENIED': 'denied'
      };
      return statusMap[status] || 'pending';
    },
    getStatusIcon(status) {
      const iconMap = {
        'PENDING': 'fa-solid fa-clock', 'CONFIRMED': 'fa-solid fa-check-circle',
        'PREPARING': 'fa-solid fa-utensils', 'READY': 'fa-solid fa-box-open',
        'OUT_FOR_DELIVERY': 'fa-solid fa-truck', 'COMPLETED': 'fa-solid fa-check-double',
        'CANCELED': 'fa-solid fa-ban', 'DENIED': 'fa-solid fa-times-circle'
      };
      return iconMap[status] || 'fa-solid fa-circle';
    },
    getPaymentIcon(method) {
      const methodLower = (method || '').toLowerCase();
      if (methodLower.includes('card') || methodLower.includes('credit') || methodLower.includes('debit')) {
        return 'fa-solid fa-credit-card';
      } else if (methodLower.includes('cash')) {
        return 'fa-solid fa-money-bill-wave';
      } else if (methodLower.includes('eft') || methodLower.includes('transfer')) {
        return 'fa-solid fa-university';
      }
      return 'fa-solid fa-wallet';
    },
    parseCookingInstructions(instructionsStr) {
      try {
        if (!instructionsStr) return [];
        const parsed = JSON.parse(instructionsStr);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        // If not valid JSON, return empty array (will show raw text instead)
        return [];
      }
    },
    printOrder() {
      // Implement print functionality
      window.print();
    }
  }
}
</script>

<style lang="scss" scoped>
/* Modal Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  overflow-y: auto;
}

/* Modal Container */
.modal-container {
  background: var(--card-bg);
  border-radius: 20px;
  max-width: 700px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalSlideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 640px) {
    max-width: 100%;
    max-height: 100vh;
    border-radius: 0;
  }
}

/* Modal Header */
.modal-header {
  position: relative;
  flex-shrink: 0;
  padding: 24px;
  background: linear-gradient(135deg, var(--accent-electric-blue) 0%, var(--accent-neon-purple) 100%);
  color: white;
  border-radius: 20px 20px 0 0;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image:
      radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.15) 0%, transparent 25%),
      radial-gradient(circle at 80% 70%, rgba(255, 255, 255, 0.1) 0%, transparent 25%);
    opacity: 0.6;
  }

  .gradient-accent {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: rgba(255, 255, 255, 0.3);
  }
}

.header-content {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;

  .header-icon {
    font-size: 2rem;
  }

  h5 {
    margin: 0 0 4px 0;
    font-size: 1.5rem;
    font-weight: 700;
  }

  .order-number-text {
    margin: 0;
    font-size: 0.95rem;
    opacity: 0.9;
  }
}

.btn-close {
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 12px;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: rotate(90deg);
  }
}

/* Modal Body */
.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px;

  /* Custom Scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(var(--accent-electric-blue-rgb), 0.3);
    border-radius: 3px;
  }
}

/* Info Sections */
.info-section {
  background: var(--secondary-button-bg);
  border-radius: 14px;
  padding: 20px;
  margin-bottom: 20px;
  border: 2px solid transparent;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: rgba(var(--accent-electric-blue-rgb), 0.2);
  }
}

.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  color: var(--text-primary);

  svg {
    color: var(--accent-electric-blue);
    font-size: 1.1rem;
  }

  h6 {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
  }
}

/* Status Grid */
.status-grid {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.status-badge-large {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &.status-pending { background: linear-gradient(135deg, var(--warning) 0%, #FFB84D 100%); color: white; }
  &.status-confirmed { background: linear-gradient(135deg, var(--accent-electric-blue) 0%, #5BB3FF 100%); color: white; }
  &.status-preparing { background: linear-gradient(135deg, var(--accent-neon-purple) 0%, #BD5AFF 100%); color: white; }
  &.status-ready { background: linear-gradient(135deg, var(--success) 0%, #5EE47B 100%); color: white; }
  &.status-delivering { background: linear-gradient(135deg, #4A90E2 0%, #357ABD 100%); color: white; }
  &.status-completed { background: linear-gradient(135deg, #28C76F 0%, #1F9D57 100%); color: white; }
  &.status-canceled, &.status-denied { background: linear-gradient(135deg, var(--danger) 0%, #FF6B6B 100%); color: white; }
}

.type-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border-radius: 12px;
  background: var(--card-bg);
  color: var(--text-primary);
  font-weight: 600;
  border: 2px solid rgba(var(--accent-electric-blue-rgb), 0.2);
}

.scheduled-info {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  color: var(--text-secondary);
  font-size: 0.9rem;

  svg {
    color: var(--accent-electric-blue);
  }
}

/* Info Grid */
.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;

  &.full-width {
    grid-column: 1 / -1;
  }

  .label {
    font-size: 0.8rem;
    color: var(--text-secondary);
    font-weight: 500;
  }

  .value {
    font-size: 0.95rem;
    color: var(--text-primary);
    font-weight: 600;
  }
}

/* Items List */
.items-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.item-card {
  background: var(--card-bg);
  border-radius: 12px;
  padding: 14px;
  border: 1px solid rgba(var(--accent-electric-blue-rgb), 0.1);
}

.item-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.item-name {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  flex: 1;
}

.item-total {
  font-size: 1.1rem;
  font-weight: 700;
  background: linear-gradient(135deg, var(--accent-electric-blue) 0%, var(--accent-neon-purple) 100%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.item-details {
  display: flex;
  gap: 12px;
  font-size: 0.85rem;
  color: var(--text-secondary);

  .item-plu {
    padding: 2px 8px;
    background: var(--secondary-button-bg);
    border-radius: 6px;
    font-weight: 500;
  }
}

.item-instructions {
  margin-top: 8px;
  padding: 12px;
  background: rgba(var(--accent-electric-blue-rgb), 0.08);
  border-left: 3px solid var(--accent-electric-blue);
  border-radius: 8px;
  font-size: 0.85rem;
  color: var(--text-primary);

  .instructions-header {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    color: var(--accent-electric-blue);
    margin-bottom: 10px;
    font-size: 0.9rem;

    svg {
      font-size: 1rem;
    }
  }

  .cooking-instructions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .cooking-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: var(--card-bg);
    border-radius: 20px;
    border: 2px solid rgba(var(--accent-electric-blue-rgb), 0.2);
    font-size: 0.85rem;
    transition: all 0.2s ease;

    &:hover {
      border-color: var(--accent-electric-blue);
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(var(--accent-electric-blue-rgb), 0.2);
    }

    .cooking-qty {
      font-weight: 700;
      color: var(--accent-electric-blue);
      font-size: 0.9rem;
    }

    .cooking-msg {
      color: var(--text-primary);
      font-weight: 500;
    }
  }

  // Fallback for non-JSON instructions
  > svg {
    color: var(--accent-electric-blue);
    flex-shrink: 0;
    margin-right: 8px;
  }
}

/* Payment Grid */
.payment-grid {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.payment-status, .payment-method {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 12px;
  font-weight: 600;
}

.payment-status {
  &.payment-paid {
    background: rgba(var(--success-rgb), 0.15);
    color: var(--success);
    border: 2px solid rgba(var(--success-rgb), 0.3);
  }

  &.payment-pending {
    background: rgba(var(--accent-orange-rgb), 0.15);
    color: var(--warning);
    border: 2px solid rgba(var(--accent-orange-rgb), 0.3);
  }
}

.payment-method {
  background: var(--card-bg);
  color: var(--text-primary);
  border: 2px solid rgba(var(--accent-electric-blue-rgb), 0.2);
}

/* Pricing Section */
.pricing-details {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.pricing-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  color: var(--text-primary);
  font-size: 0.95rem;

  &:not(:last-child) {
    border-bottom: 1px solid rgba(var(--accent-electric-blue-rgb), 0.1);
  }

  &.total {
    padding-top: 16px;
    margin-top: 8px;
    border-top: 2px solid rgba(var(--accent-electric-blue-rgb), 0.2);
    font-weight: 700;
    font-size: 1.1rem;

    .total-amount {
      font-size: 1.5rem;
      background: linear-gradient(135deg, var(--accent-electric-blue) 0%, var(--accent-neon-purple) 100%);
      background-clip: text;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
  }
}

/* Notes Box */
.notes-box {
  background: var(--card-bg);
  border-left: 4px solid var(--accent-electric-blue);
  padding: 14px;
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 0.95rem;
  line-height: 1.6;
}

/* Timestamps */
.timestamps {
  display: flex;
  gap: 20px;
  justify-content: center;
  padding: 16px 0 0 0;
  border-top: 1px solid rgba(var(--accent-electric-blue-rgb), 0.1);
  flex-wrap: wrap;
}

.timestamp-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-secondary);
  font-size: 0.85rem;

  svg {
    color: var(--accent-electric-blue);
  }
}

/* Modal Footer */
.modal-footer {
  flex-shrink: 0;
  padding: 20px 24px;
  border-top: 1px solid rgba(var(--accent-electric-blue-rgb), 0.1);
  display: flex;
  gap: 12px;
  justify-content: flex-end;

  @media (max-width: 640px) {
    flex-direction: column;
  }
}

.btn-secondary, .btn-primary {
  padding: 12px 24px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  border: none;
}

.btn-secondary {
  background: var(--secondary-button-bg);
  color: var(--text-primary);

  &:hover {
    background: var(--secondary-button-bg-dark);
  }
}

.btn-primary {
  background: linear-gradient(135deg, var(--accent-electric-blue) 0%, var(--accent-neon-purple) 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(var(--accent-electric-blue-rgb), 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(var(--accent-electric-blue-rgb), 0.4);
  }
}

/* Animations */
@keyframes modalSlideUp {
  from {
    opacity: 0;
    transform: translateY(40px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-fade-enter-active, .modal-fade-leave-active {
  transition: opacity 0.3s ease;

  .modal-container {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
}

.modal-fade-enter-from, .modal-fade-leave-to {
  opacity: 0;

  .modal-container {
    transform: translateY(40px) scale(0.95);
  }
}
</style>
